import { api, cachedRequest } from '../apiClient';

export const eventService = {
  getEvents: async (params: {
    page?: number;
    search?: string;
    status?: string;
    ordering?: string;
  } = {}, forceRefresh: boolean = false) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.ordering) queryParams.append('ordering', params.ordering);
    
    const cacheKey = `events_${queryParams.toString()}`;
    
    return cachedRequest(
      cacheKey,
      async () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('Making API request to /events/ for events list');
        }
        const response = await api.get(`/events/?${queryParams.toString()}`);
        if (process.env.NODE_ENV === 'development') {
          console.log('Events API response received:', {
            eventsCount: response.data.events?.length || 0,
            totalEvents: response.data.pagination?.total_events || 0,
            currentPage: response.data.pagination?.current_page || 1
          });
        }
        return response.data;
      },
      2 * 60 * 1000, // 2 minutes
      forceRefresh
    );
  },

  getEventDetail: async (slug: string, options?: {
    forceRefresh?: boolean;
    exclude?: string[];
    include?: string[];
  }) => {
    const { forceRefresh = false, exclude = [], include = [] } = options || {};
    
    // Build cache key with include/exclude params
    const params = [];
    if (exclude.length > 0) params.push(`exclude:${exclude.sort().join(',')}`);
    if (include.length > 0) params.push(`include:${include.sort().join(',')}`);
    const cacheKey = `event_detail_${slug}${params.length > 0 ? '_' + params.join('_') : ''}`;
    
    return cachedRequest(
      cacheKey,
      async () => {
        if (process.env.NODE_ENV === 'development') {
          console.log(`Making API request to /events/${slug}/ for event details`, { exclude, include });
        }
        
        // Build query params
        const queryParams = new URLSearchParams();
        if (exclude.length > 0) queryParams.append('exclude', exclude.join(','));
        if (include.length > 0) queryParams.append('include', include.join(','));
        
        const url = `/events/${slug}/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        const response = await api.get(url);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Event Detail API response received:', {
            eventTitle: response.data.title,
            eventSlug: response.data.slug,
            categoriesCount: response.data.categories?.length || 0,
            imagesCount: response.data.images?.length || 0,
            addonsCount: response.data.addons?.length || 0,
            userHasAccess: response.data.user_has_access
          });
        }
        return response.data;
      },
      5 * 60 * 1000, // 5 minutes
      forceRefresh
    );
  },
  
  // Progressive loading methods
  getEventDetailBasic: async (slug: string, forceRefresh: boolean = false) => {
    // Load only essential data (exclude heavy sections)
    return eventService.getEventDetail(slug, {
      forceRefresh,
      exclude: ['images', 'addons', 'memories', 'reviews', 'partners', 'creatives', 'sponsors']
    });
  },
  
  getEventDetailWithAddons: async (slug: string, forceRefresh: boolean = false) => {
    // Load essential + addons
    return eventService.getEventDetail(slug, {
      forceRefresh,
      exclude: ['images', 'memories', 'reviews', 'partners', 'creatives', 'sponsors']
    });
  },
  
  getEventDetailWithImages: async (slug: string, forceRefresh: boolean = false) => {
    // Load essential + images
    return eventService.getEventDetail(slug, {
      forceRefresh,
      exclude: ['addons', 'memories', 'reviews', 'partners', 'creatives', 'sponsors']
    });
  },
  
  getEventDetailFull: async (slug: string, forceRefresh: boolean = false) => {
    // Load everything
    return eventService.getEventDetail(slug, { forceRefresh });
  },

  // Seated Registration API methods
  getSeatMapData: async (eventSlug: string, categoryId: number) => {
    const response = await api.get(`/register/seated/seat-map/`, {
      params: {
        event_slug: eventSlug,
        category_id: categoryId
      }
    });
    return response.data;
  },

  holdSeats: async (seatIds: number[]) => {
    const response = await api.post('/register/seated/hold-seats/', {
      seat_ids: seatIds
    });
    return response.data;
  },

  releaseSeats: async (seatIds: number[]) => {
    const response = await api.post('/register/seated/release-seats/', {
      seat_ids: seatIds
    });
    return response.data;
  },

  submitSeatedRegistration: async (data: {
    event_slug: string;
    category_id: number;
    seats: Array<{
      seat_id: number;
      name: string;
      email: string;
      phone: string;
      custom_fields?: Record<string, any>;
    }>;
  }) => {
    const response = await api.post('/register/seated/', data);
    return response.data;
  },

  submitAutomaticSeatedRegistration: async (data: {
    event_slug: string;
    category_id: number;
    quantity: number;
    attendees: Array<{
      name: string;
      email: string;
      phone: string;
      custom_fields?: Record<string, any>;
    }>;
  }) => {
    const response = await api.post('/register/seated/automatic/', data);
    return response.data;
  },

  // Cluster Seated Registration API
  submitClusterSeatedRegistration: async (data: {
    event_slug: string;
    cluster_id: number;
    quantity: number;
    name: string;
    email: string;
    phone: string;
    custom_fields?: Record<string, any>;
    additional_attendees?: Array<{
      name: string;
      email?: string;
      phone?: string;
      custom_fields?: Record<string, any>;
    }>;
  }) => {
    const response = await api.post('/register/cluster-seated/', data);
    return response.data;
  },

  // Modular event API endpoints
  getEventBasic: async (slug: string, forceRefresh: boolean = false) => {
    const cacheKey = `event_basic_${slug}`;
    
    return cachedRequest(
      cacheKey,
      async () => {
        if (process.env.NODE_ENV === 'development') {
          console.log(`Making API request to /events/${slug}/basic/ for basic event info`);
        }
        const response = await api.get(`/events/${slug}/basic/`);
        if (process.env.NODE_ENV === 'development') {
          console.log('Event Basic API response received:', {
            eventTitle: response.data.title,
            eventSlug: response.data.slug,
            template: response.data.event_page_template
          });
        }
        return response.data;
      },
      10 * 60 * 1000, // 10 minutes - basic info changes less frequently
      forceRefresh
    );
  },

  getEventContent: async (slug: string, forceRefresh: boolean = false) => {
    const cacheKey = `event_content_${slug}`;
    
    return cachedRequest(
      cacheKey,
      async () => {
        if (process.env.NODE_ENV === 'development') {
          console.log(`Making API request to /events/${slug}/content/ for event content`);
        }
        const response = await api.get(`/events/${slug}/content/`);
        if (process.env.NODE_ENV === 'development') {
          console.log('Event Content API response received:', {
            eventSlug: response.data.slug,
            imagesCount: response.data.images?.length || 0,
            hasAbout: !!response.data.about,
            hasPolicy: !!response.data.policy
          });
        }
        return response.data;
      },
      5 * 60 * 1000, // 5 minutes
      forceRefresh
    );
  },

  getEventCategories: async (slug: string, forceRefresh: boolean = false) => {
    const cacheKey = `event_categories_${slug}`;
    
    return cachedRequest(
      cacheKey,
      async () => {
        if (process.env.NODE_ENV === 'development') {
          console.log(`Making API request to /events/${slug}/categories/ for event categories`);
        }
        const response = await api.get(`/events/${slug}/categories/`);
        if (process.env.NODE_ENV === 'development') {
          console.log('Event Categories API response received:', {
            eventSlug: response.data.slug,
            clustersCount: response.data.clusters?.length || 0,
            addonsCount: response.data.addons?.length || 0
          });
        }
        return response.data;
      },
      2 * 60 * 1000, // 2 minutes - categories might change more frequently
      forceRefresh
    );
  },

  getEventRelated: async (slug: string, forceRefresh: boolean = false) => {
    const cacheKey = `event_related_${slug}`;
    
    return cachedRequest(
      cacheKey,
      async () => {
        if (process.env.NODE_ENV === 'development') {
          console.log(`Making API request to /events/${slug}/related/ for related event data`);
        }
        const response = await api.get(`/events/${slug}/related/`);
        if (process.env.NODE_ENV === 'development') {
          console.log('Event Related API response received:', {
            eventSlug: response.data.slug,
            partnersCount: response.data.partners?.length || 0,
            creativesCount: response.data.creatives?.length || 0,
            sponsorsCount: response.data.sponsors?.length || 0
          });
        }
        return response.data;
      },
      10 * 60 * 1000, // 10 minutes - related data changes less frequently
      forceRefresh
    );
  }
};

